/**
 * Constantes para el CEO Executive Dashboard v2.
 */

// ── HEDIS Targets ─────────────────────────────────────────────
export const HEDIS_TARGETS = {
  "Colorectal Cancer Screening": 85,
  "Breast Cancer Screening": 85,
  "Cervical Cancer Screening, 21-64 years": 75,
  "Controlling High Blood Pressure": 80,
  "Hemoglobin A1c (HbA1c) Testing": 85,
  "Statin Therapy for Patients with Diabetes": 80,
  "Statin Therapy for Patients with Cardiovascular Disease": 80,
  "Follow-Up After Emergency Department Visit for Mental Illness, 30 Days": 60,
  "Follow-Up After Hospitalization for Mental Illness, 30 Days": 60,
  _default: 75,
};

export function getHedisTarget(measureName) {
  return HEDIS_TARGETS[measureName] ?? HEDIS_TARGETS._default;
}

export function getMeasureStatus(current, target) {
  if (current >= target) return "on_track";
  if (current >= target * 0.9) return "at_risk";
  return "behind";
}

// ── Health Status Colors (para distribution bar) ──────────────
export const HEALTH_STATUS_COLORS = {
  Well: "#10B981",
  Acute: "#F59E0B",
  Chronic: "#8B5CF6",
  "At Risk": "#EF4444",
  Complex: "#DC2626",
  "End of Life": "#1F2937",
  "No Status": "#D1D5DB",
};

export const HEALTH_STATUS_ORDER = [
  "Well",
  "Acute",
  "Chronic",
  "At Risk",
  "Complex",
  "End of Life",
  "No Status",
];

// ── Chart Colors ──────────────────────────────────────────────
export const COST_CHART_COLORS = [
  "#1E40AF", // Inpatient
  "#7C3AED", // Pharmacy
  "#2563EB", // Outpatient
  "#0891B2", // Professional
  "#DC2626", // ER
  "#6B7280", // Ancillary
  "#D1D5DB", // Virtual
];

export const COST_LINE_COLORS = {
  total: "#1E40AF",
  inpatient: "#DC2626",
  pharmacy: "#7C3AED",
};

export const COST_BAR_COLORS = {
  inpatient: "#1E40AF",
  pharmacy: "#7C3AED",
  er: "#DC2626",
  other: "#D1D5DB",
};

export const CONDITION_TYPE_COLORS = {
  Dropped: { bg: "#FEE2E2", text: "#DC2626" },
  Suspect: { bg: "#FEF3C7", text: "#D97706" },
  Pharmacy: { bg: "#DBEAFE", text: "#1D4ED8" },
};

// ── Disposition Colors (v2) ───────────────────────────────────
export const DISPOSITION_COLORS = {
  "Not Addressed": { bg: "#FEE2E2", text: "#991B1B" },
  "Current Condition": { bg: "#D1FAE5", text: "#065F46" },
  "Non-Existent": { bg: "#F3F4F6", text: "#374151" },
  "Insufficient Details": { bg: "#FEF3C7", text: "#92400E" },
  "Acute Episode": { bg: "#DBEAFE", text: "#1E40AF" },
};

// ── Center Performance Thresholds ─────────────────────────────
export const CENTER_THRESHOLDS = {
  noVisitPct: { good: 50, warning: 80 },
  gapCompliance: { good: 40, warning: 20 },
  cmRatio: 20,
};

export function getCellColor(value, metric) {
  const t = CENTER_THRESHOLDS[metric];
  if (!t) return null;
  if (metric === "noVisitPct") {
    if (value < t.good) return { bg: "#D1FAE5", text: "#065F46" };
    if (value <= t.warning) return { bg: "#FEF3C7", text: "#92400E" };
    return { bg: "#FEE2E2", text: "#991B1B" };
  }
  if (metric === "gapCompliance") {
    if (value >= t.good) return { bg: "#D1FAE5", text: "#065F46" };
    if (value >= t.warning) return { bg: "#FEF3C7", text: "#92400E" };
    return { bg: "#FEE2E2", text: "#991B1B" };
  }
  return null;
}

// ── SP Visit Diff Thresholds ──────────────────────────────────
export const SP_DIFF_THRESHOLDS = {
  high: 10,
  medium: 5,
};

// ── Cost Formatting ───────────────────────────────────────────
export function formatCost(value) {
  if (value == null) return "$0";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

export function formatNumber(num) {
  if (num == null) return "0";
  return num.toLocaleString();
}

// ── Trends ────────────────────────────────────────────────────
export const TREND_MONTHS = 18;

// ── Tables ────────────────────────────────────────────────────
export const TABLE_DEFAULTS = {
  topPCPs: 5,
  topFacilities: 5,
  topProviders: 10,
  topHCCs: 10,
  recaptureByPCP: 10,
  measures: 10,
};

// ── Cost View Modes ───────────────────────────────────────────
export const COST_VIEW_MODES = ["all", "monthly", "byLocation"];

// ── Engagement Colors ─────────────────────────────────────────
export const ENGAGEMENT_COLORS = {
  "<3 Months": "#10B981",
  "3 Months": "#3B82F6",
  "6 Months": "#8B5CF6",
  "9 Months": "#F59E0B",
  "12+ Months": "#EF4444",
};

// ── Workflow Status Chip Colors (v2) ──────────────────────────
export const WORKFLOW_CHIP_COLORS = {
  // Positivos -> Verde
  SCHEDULED: { bg: "#D1FAE5", text: "#065F46" },
  EVIDENCE_UPLOADED: { bg: "#D1FAE5", text: "#065F46" },
  VERIFIED: { bg: "#D1FAE5", text: "#065F46" },
  CLOSED_BY_USER: { bg: "#D1FAE5", text: "#065F46" },
  ALREADY_SCHEDULED: { bg: "#D1FAE5", text: "#065F46" },
  COMPLETE: { bg: "#D1FAE5", text: "#065F46" },
  CLOSED_AVAILITY: { bg: "#D1FAE5", text: "#065F46" },
  CARE_PLAN_ESTABLISHED: { bg: "#D1FAE5", text: "#065F46" },
  NOTES_SIGNED: { bg: "#D1FAE5", text: "#065F46" },
  // En proceso -> Azul/Ambar
  IN_PROGRESS: { bg: "#DBEAFE", text: "#1E40AF" },
  CONTACTED: { bg: "#DBEAFE", text: "#1E40AF" },
  CONTACT_ATTEMPTED: { bg: "#FEF3C7", text: "#92400E" },
  PENDING: { bg: "#FEF3C7", text: "#92400E" },
  FOLLOW_UP_REQUIRED: { bg: "#FEF3C7", text: "#92400E" },
  DOCTOR_INFO: { bg: "#FEF3C7", text: "#92400E" },
  // Sin contacto -> Rojo
  NO_PHONE: { bg: "#FEE2E2", text: "#991B1B" },
  REFUSED: { bg: "#FEE2E2", text: "#991B1B" },
  OTHER_LANGUAGE: { bg: "#FEE2E2", text: "#991B1B" },
  FAILED_TO_ADDRESS: { bg: "#FEE2E2", text: "#991B1B" },
  // Administrativos -> Gris
  EXEMPT: { bg: "#F3F4F6", text: "#374151" },
  NOT_OUR_PATIENT: { bg: "#F3F4F6", text: "#374151" },
  OTHER_CARRIER: { bg: "#F3F4F6", text: "#374151" },
  NOT_IN_ROSTER: { bg: "#F3F4F6", text: "#374151" },
  NOT_ADDRESSED_PCP: { bg: "#F3F4F6", text: "#374151" },
  INCORRECT_INFO: { bg: "#F3F4F6", text: "#374151" },
  CANCEL_INSURANCE: { bg: "#F3F4F6", text: "#374151" },
  OUT_OF_AREA: { bg: "#F3F4F6", text: "#374151" },
  RELOCATED: { bg: "#F3F4F6", text: "#374151" },
  DECEASED: { bg: "#F3F4F6", text: "#374151" },
  OTHER: { bg: "#F3F4F6", text: "#374151" },
};

// ── Priority Colors ───────────────────────────────────────────
export const PRIORITY_COLORS = {
  HIGH: { bg: "#FEE2E2", text: "#991B1B" },
  MEDIUM: { bg: "#FEF3C7", text: "#92400E" },
  LOW: { bg: "#D1FAE5", text: "#065F46" },
};

// ── Performance Score Levels ──────────────────────────────────
export function getPerformanceLevel(score) {
  if (score >= 90) return { label: "Outstanding", color: "#065F46", bg: "#D1FAE5" };
  if (score >= 80) return { label: "Good", color: "#1E40AF", bg: "#DBEAFE" };
  if (score >= 70) return { label: "Needs Improvement", color: "#92400E", bg: "#FEF3C7" };
  return { label: "Critical", color: "#991B1B", bg: "#FEE2E2" };
}

// ── Task Status Display ───────────────────────────────────────
export const TASK_STATUS_DISPLAY = {
  completed: { label: "Completed", color: "#065F46", bg: "#D1FAE5" },
  in_progress: { label: "In Progress", color: "#1E40AF", bg: "#DBEAFE" },
  pending: { label: "Pending", color: "#92400E", bg: "#FEF3C7" },
  overdue: { label: "Overdue", color: "#991B1B", bg: "#FEE2E2" },
};
